import React, { useCallback, useContext, useState } from "react";
import { AnnotatedPlan } from "types/explain";

export type ExplainAspect =
  | "Est. Cost"
  | "Runtime"
  | "Rows"
  | "Buffers"
  | "Reads"
  | "Writes";

const ExplainSubAspects = {
  "Est. Cost": [],
  Runtime: [],
  Rows: ["Actual", "Estimated"],
  Buffers: ["Hit + Read", "Dirtied"],
  Reads: ["Bytes", "Time"],
  Writes: ["Bytes", "Time"],
} as const;

export type ExplainSubAspect =
  (typeof ExplainSubAspects)[ExplainAspect][number];

type SelectedSubAspects = {
  "Est. Cost"?: undefined;
  Runtime?: undefined;
  Rows: (typeof ExplainSubAspects)["Rows"][number];
  Buffers: (typeof ExplainSubAspects)["Buffers"][number];
  Reads: (typeof ExplainSubAspects)["Reads"][number];
  Writes: (typeof ExplainSubAspects)["Writes"][number];
};

type ExplainAspectSelection = {
  current: ExplainAspect;
  subAspects: SelectedSubAspects;
};

const initialValue: ExplainAspectSelection = {
  current: "Est. Cost",
  subAspects: {
    Rows: "Actual",
    Buffers: "Hit + Read",
    Reads: "Bytes",
    Writes: "Bytes",
  },
};

const ExplainAspectContext = React.createContext(initialValue);

const ExplainSetAspectContext = React.createContext<
  (aspect: ExplainAspect, subAspect?: ExplainSubAspect) => void
>(() => {});

export function WithExplainAspect({
  children,
  initialAspect,
}: {
  children: React.ReactNode;
  initialAspect: ExplainAspect;
}) {
  const [state, setState] = useState({
    ...initialValue,
    current: initialAspect,
  });
  const updateAspectSelection = useCallback(
    (aspect: ExplainAspect, subAspect?: ExplainSubAspect) => {
      setState((selection) => ({
        current: aspect,
        subAspects: {
          ...selection.subAspects,
          [aspect]: subAspect ?? selection.subAspects[aspect],
        },
      }));
    },
    [],
  );
  return (
    <ExplainSetAspectContext.Provider value={updateAspectSelection}>
      <ExplainAspectContext.Provider value={state}>
        {children}
      </ExplainAspectContext.Provider>
    </ExplainSetAspectContext.Provider>
  );
}

export function useExplainAspect() {
  const state = useContext(ExplainAspectContext);
  return [state.current, state.subAspects[state.current]] as const;
}

export function useSetExplainAspect() {
  return useContext(ExplainSetAspectContext);
}

export function initialExplainAspect(
  plan: AnnotatedPlan | undefined,
  planB?: AnnotatedPlan,
) {
  if (!plan) {
    return "Runtime";
  }
  return lacksMetric(plan, "Actual Rows") || lacksMetric(planB, "Actual Rows")
    ? "Est. Cost"
    : lacksMetric(plan, "Actual Total Time") ||
      lacksMetric(planB, "Actual Total Time")
    ? "Rows"
    : "Runtime";
}

function lacksMetric(
  plan: AnnotatedPlan | undefined,
  metric: "Actual Rows" | "Actual Total Time",
) {
  return !!plan && plan.plan[0]["Plan"][metric] == undefined;
}

export function getSubAspects(aspect: ExplainAspect) {
  return ExplainSubAspects[aspect];
}

export function labelAspect(
  aspect: ExplainAspect,
  subAspect: ExplainSubAspect,
) {
  switch (aspect) {
    case "Est. Cost":
    case "Runtime":
      return aspect;
    case "Rows":
      switch (subAspect) {
        case "Actual":
          return "Actual Rows";
        case "Estimated":
          return "Est. Rows";
        default:
          return undefined;
      }
    case "Buffers":
      switch (subAspect) {
        case "Hit + Read":
        case "Dirtied":
          return subAspect;
        default:
          return undefined;
      }
    case "Reads":
      switch (subAspect) {
        case "Bytes":
          return "Bytes Read";
        case "Time":
          return "I/O Read Time";
        default:
          return undefined;
      }
    case "Writes":
      switch (subAspect) {
        case "Bytes":
          return "Bytes Written";
        case "Time":
          return "I/O Write Time";
        default:
          return undefined;
      }
  }
}
